import type * as stripeJs from '@stripe/stripe-js';
import type {
  PaymentRequest,
  PaymentRequestWallet,
} from '@stripe/stripe-js/types/stripe-js/payment-request';

import { sha256 } from 'js-sha256';
import { v4 as uuidv4 } from 'uuid';

import logEvent from 'analytics/logEvent';
import TagManager from '@sooro-io/react-gtm-module';

import { isAuthSelector, profileSelector } from 'domains/auth/auth.selectors';
import { QueryParser } from 'domains/common/queryParser';
import type { StripeProcessing } from 'domains/payments/stripeProcessing';
import type { ProductsTypeV3 } from 'domains/products/products.types';
import type { NextRouter } from 'next/router';

import type { AppStore } from 'redux-store';
import { openModal } from 'redux-store/slices/ui';

import { actions as mobilePaymentActions } from './index';
import { MobilePaymentStateEnum } from './interfaces';
import { mobilePayment } from './reducer';
import {
  modalTypeSelector,
  paymentTypeSelector,
  productSelector,
  walletCardIdSelector,
  walletCardSelector,
} from './selectors';

// service

export class PaymentMobileService {
  constructor(private readonly stripeProcessing: StripeProcessing) {}

  showModal({
    store,
    product,
    state = MobilePaymentStateEnum.DEFAULT,
    modalType = 'Auth',
  }: {
    store: AppStore;
    product: ProductsTypeV3;
    state?: MobilePaymentStateEnum;
    modalType?: 'Auth' | 'Registration';
  }): void {
    store.dispatch(mobilePaymentActions.show({ product, state, modalType }));
  }

  setProduct({
    store,
    product,
  }: {
    store: AppStore;
    product: ProductsTypeV3;
  }): void {
    store.dispatch(mobilePaymentActions.setProduct({ product }));
  }

  setLoadingCard({
    store,
    isLoading,
  }: {
    store: AppStore;
    isLoading: boolean;
  }): void {
    store.dispatch(mobilePaymentActions.setLoadingCard({ isLoading }));
  }

  setProcessingStart({ store }: { store: AppStore }): void {
    const state = store.getState();
    const product = productSelector(state);
    const paymentType = paymentTypeSelector(state);
    const currentWalletId = walletCardIdSelector(state);
    const query = new QueryParser(window.location.search);
    const coupon = query.get('coupon');
    logEvent('Web_Payment_Pay_Button_Clicked', {
      coupon,
      'coupon%': product?.discount,
      tariff: product?.interval,
      plan_id: product?.id,
      payment_system: paymentType,
      pay_saved_card: paymentType === 'card' && currentWalletId !== null,
    });
    logEvent('Web_Payment_Processing_Screen_Viewed');
    store.dispatch(mobilePaymentActions.setProcessingStart());
  }

  private setProcessingEnd({ store }: { store: AppStore }): void {
    store.dispatch(mobilePaymentActions.setProcessingEnd());
  }

  setLoadingButton({
    store,
    isLoading,
  }: {
    store: AppStore;
    isLoading: boolean;
  }): void {
    store.dispatch(mobilePaymentActions.setLoadingButton({ isLoading }));
  }

  onClickOverlay({ store }: { store: AppStore }): void {
    logEvent('Web_Payment_Action_Sheet_Closed', { type: 'card_widget' });
    this.setNewState({ store, newState: MobilePaymentStateEnum.DEFAULT });
  }

  setPaymentType({
    paymentType,
    store,
  }: {
    paymentType: string;
    store: AppStore;
  }): void {
    store.dispatch(mobilePaymentActions.setPaymentType(paymentType));
  }

  setNewState({
    newState,
    store,
  }: {
    newState: MobilePaymentStateEnum;
    store: AppStore;
  }): void {
    if (newState === MobilePaymentStateEnum.CARD) {
      const state = store.getState();
      const product = productSelector(state);
      const query = new QueryParser(window.location.search);
      const coupon = query.get('coupon');
      const isAuthenticated = isAuthSelector(state);
      const modalType = modalTypeSelector(state);
      const token = query.get('token');

      logEvent('Web_Payment_Continue_Button_Clicked', {
        tariff: product?.interval,
        coupon,
        'coupon%': product?.discount,
      });

      if (!(token || isAuthenticated)) {
        if (product) {
          query.set({ selected: `${product.id}` });
        }

        store.dispatch(
          openModal({
            type: modalType,
            redirectUrl: `${window.location.pathname}${query.toString()}`,
          }),
        );

        return;
      }
    }

    store.dispatch(
      mobilePayment.actions.setState({
        newState,
      }),
    );
  }

  setWalletCard({ store, id }: { store: AppStore; id: string | null }): void {
    const state = store.getState();
    const currentWalletId = walletCardIdSelector(state);
    const walletCardId = currentWalletId === id ? null : id;

    if (walletCardId) {
      logEvent('Web_Payment_Card_Selected');
    }

    store.dispatch(
      mobilePayment.actions.setWalletCard({
        walletCardId,
      }),
    );
  }

  onClickPayByButton({
    store,
    e,
    paymentSystem,
  }: {
    store: AppStore;
    paymentSystem: string | null;
    e: stripeJs.StripePaymentRequestButtonElementClickEvent;
  }): void {
    const state = store.getState();
    const product = productSelector(state);
    const query = new QueryParser(window.location.search);
    const isAuthenticated = isAuthSelector(state);
    const modalType = modalTypeSelector(state);
    const token = query.get('token');

    if (!(token || isAuthenticated)) {
      e.preventDefault();
      if (product) {
        query.set({ selected: `${product.id}` });
      }

      store.dispatch(
        openModal({
          type: modalType,
          redirectUrl: `${window.location.pathname}${query.toString()}`,
        }),
      );

      return;
    }

    const coupon = query.get('coupon');

    logEvent('Web_Payment_Pay_Button_Clicked', {
      coupon,
      'coupon%': product?.discount,
      tariff: product?.interval,
      plan_id: product?.id,
      payment_system: paymentSystem,
      pay_saved_card: null,
    });
  }

  async onPayByButton({
    store,
    stripe,
    routing,
  }: {
    stripe: stripeJs.Stripe;
    store: AppStore;
    routing: NextRouter;
  }): Promise<[PaymentRequest, string | null] | null> {
    this.setLoadingCard({ isLoading: true, store });
    this.setLoadingButton({ isLoading: true, store });
    const state = store.getState();
    const product = productSelector(state);
    const coupon = routing.query.coupon as string | undefined;

    const pathname = window.location.pathname.endsWith('/') ? window.location.pathname.slice(0, -1) : window.location.pathname;

    const profile = profileSelector(state);

    if (!product) {
      console.error('product is undefined', product);

      return null;
    }

    const currentPrice = product.discount_price || product.price;
    const plan: {
      id: number;
      interval: string;
      discount: number;
      price: number;
    } = {
      id: product.id,
      interval: product.interval,
      discount: product.discount || 0,
      price: currentPrice,
    };
    const info: {
      currency: string;
      title: string;
      totalPrice: number;
    } = {
      currency: product.currency,
      title: product.title,
      totalPrice: currentPrice,
    };

    const onPay = async ({
      paymentName,
      isSuccess,
    }: {
      paymentName: PaymentRequestWallet | string;
      isSuccess: boolean;
    }): Promise<void> => {
      logEvent('Web_Payment_Pay_Button_Clicked', {
        coupon,
        'coupon%': product.discount,
        tariff: product.interval,
        plan_id: product.id,
        payment_system: 'card',
        pay_saved_card: paymentName,
      });

      const query = new QueryParser(window.location.search);

      if (!isSuccess) {
        await routing.push(
          `${pathname}/fail${query.toString()}`,
        );

        return;
      }

      const product_id = routing.query.product_id as string || `${product.id}`;

      await this.sendOnboardingStat({
        interval: product.interval,
        price: currentPrice,
        currency: product.currency,
        product_id: product_id,
        email: profile.profile?.email
      });

      await routing.push(
        `${pathname}/success${query.toString()}`,
      );
    };

    const onPayBefore = (): void => {
      this.setLoadingCard({ isLoading: true, store });
      this.setProcessingStart({ store });
    };

    try {
      const result = await this.stripeProcessing.processingButton({
        coupon,
        plan,
        info,
        stripe,
        store,
        onPay,
        onPayBefore,
      });

      if (result && result[1]) {
        const paymentType = result[1];
        this.setPaymentType({ paymentType, store });
      }

      return result;
    } catch (e) {
      const query = new QueryParser(window.location.search);
      await routing.push(`${pathname}/fail${query.toString()}`);

      return null;
    }
  }

  async onPayByCard({
    stripe,
    elements,
    store,
    routing,
  }: {
    stripe: stripeJs.Stripe;
    elements: stripeJs.StripeElements;
    store: AppStore;
    routing: NextRouter;
  }): Promise<void> {
    const query = new QueryParser(window.location.search);

    const pathname = window.location.pathname.endsWith('/') ? window.location.pathname.slice(0, -1) : window.location.pathname;

    try {
      this.setPaymentType({ paymentType: 'card', store });
      this.setProcessingStart({ store });
      this.setLoadingCard({ isLoading: true, store });
      this.setLoadingButton({ isLoading: true, store });

      const state = store.getState();
      const walletCardId = walletCardSelector(state);
      const product = productSelector(state);
      const coupon = routing.query.coupon as string | undefined;

      const profile = profileSelector(state);

      if (!product) {
        console.error('product is undefined', product);

        return;
      }

      const product_id = routing.query.product_id as string || `${product.id}`;

      const currentPrice = product.discount_price || product.price;
      const plan: {
        id: number;
        interval: string;
        discount: number;
        price: number;
      } = {
        id: product.id,
        interval: product.interval,
        discount: product.discount || 0,
        price: currentPrice,
      };
      const processingResult = await this.stripeProcessing.processing({
        coupon,
        walletCardId,
        plan,
        store,
        stripe,
        elements,
      });

      if (!processingResult) {
        await routing.push(
          `${pathname}/fail${query.toString()}`,
        );

        return;
      }

      if (processingResult.result?.status === 'requires_action') {
        try {
          logEvent('Web_Payment_3d_Secure_Redirected');

          query.set({ interval: product.interval, price: `${currentPrice}` });

          // @ts-ignore
          const confirmPayment = await stripe.confirmPayment({
            clientSecret: processingResult.result.client_secret,
            confirmParams: {
              return_url: `${window.location.origin}${
                pathname
              }/success${query.toString()}`,
            },
            redirect: 'always',
          });

          if (confirmPayment.error) {
            logEvent('Web_Payment_3d_Secure_Failed');
            await routing.push(
              `${pathname}/fail${query.toString()}`,
            );

            return;
          }
        } catch (e) {
          await routing.push(
            `${pathname}/fail${query.toString()}`,
          );

          return;
        }
      }

      await this.sendOnboardingStat({
        interval: product.interval,
        price: currentPrice,
        currency: product.currency,
        product_id: product_id,
        email: profile.profile?.email,
      });

      await routing.push(
        `${pathname}/success${query.toString()}`,
      );
    } catch (e) {
      await routing.push(`${pathname}/fail${query.toString()}`);
    }
  }

  sendOnboardingStat({
    interval,
    price,
    currency = '',
    product_id = '',
    email = '',
  }: {
    interval: string;
    price: number | string;
    currency?: string;
    product_id?: string;
    email?: string;
  }): void {
    try {
      const event_id = uuidv4();
      currency = currency.toUpperCase();
      (window as any).fbq?.('track', 'Purchase', { value: price, currency: currency }, { eventID: event_id });
      (window as any).fbq?.('trackCustom', 'WebFunnelPurchase', { value: price, currency: currency }, { eventID: event_id } );

      (window as any).ttq?.track('CompletePayment', { value: price, currency: currency, content_id: product_id }, { event_id: event_id });

      if (/survey/.test(window.location.pathname) === true) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'Purchase',
            event_id: event_id,
            eventModel: {
              value: price,
              currency: currency,
              user_data: {
                em: sha256(email),
                email: sha256(email),
                email_address: sha256(email),
              }
            },
          },
          dataLayerName: 'dataLayer'
        });
      }

      const query = new QueryParser(window.location.search);
      const onboardId = query.get('onboardId');

      if (!onboardId) {
        return;
      }

      const beaconBody = {
        planType: interval,
        planPrice: price,
        onboardId,
      };
      const beaconHeaders = {
        type: 'application/json',
      };
      const blob = new Blob([JSON.stringify(beaconBody)], beaconHeaders);
      navigator.sendBeacon('/api/log', blob);
    } catch (e) {}
  }
}
